/**
 * Module-scoped runtime state for MapTimeSeries redraw deduplication.
 * Avoids cross-instance coupling via window.* for selection/update tracking.
 */

export type MapTimeSeriesRuntimeState = {
  lastSelectedEvents: unknown[];
  lastSelectedRange: unknown[];
  lastSelectedRanges: unknown[];
  lastCutEvents: unknown[];
  lastMaptype: string;
  lastTheme: string;
  lastValuesLength: number;
  lastMapDataLength: number;
  /** Race/leg filter + filtered timeline extent (detects filter changes with same raw length). */
  lastTimelineFilterSig: string;
  lastSelectedTime: Date | null;
  updateTimeout: ReturnType<typeof setTimeout> | null;
};

export const mapTimeSeriesRuntime: MapTimeSeriesRuntimeState = {
  lastSelectedEvents: [],
  lastSelectedRange: [],
  lastSelectedRanges: [],
  lastCutEvents: [],
  lastMaptype: '',
  lastTheme: '',
  lastValuesLength: 0,
  lastMapDataLength: 0,
  lastTimelineFilterSig: '',
  lastSelectedTime: null,
  updateTimeout: null,
};

function eventIdsFromSelection(events: unknown[] | undefined): string {
  if (!events?.length) return '';
  return events
    .map((item) => {
      if (item == null) return '';
      if (typeof item === 'number' || typeof item === 'string') return String(item);
      if (typeof item === 'object' && 'event_id' in item) {
        return String((item as { event_id: unknown }).event_id);
      }
      return JSON.stringify(item);
    })
    .join(',');
}

/** Stable signature for selection arrays — avoids JSON.stringify on every reactive tick. */
export function selectionArraySignature(events: unknown[] | undefined): string {
  if (!events?.length) return '0';
  return `${events.length}:${eventIdsFromSelection(events)}`;
}

export function selectionArraysEqual(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  return selectionArraySignature(a) === selectionArraySignature(b);
}

/** Signature for event highlight ranges (includes color for performance-page sync). */
export function selectedRangesSignature(ranges: unknown[] | undefined): string {
  if (!ranges?.length) return '0';
  return ranges
    .map((item) => {
      if (item == null || typeof item !== 'object') return '';
      const r = item as Record<string, unknown>;
      return `${r.event_id ?? ''}:${r.start_time ?? ''}:${r.end_time ?? ''}:${r.color ?? ''}`;
    })
    .join('|');
}

export function selectedRangesEqual(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  return selectedRangesSignature(a) === selectedRangesSignature(b);
}

export function cloneSelectionSnapshot<T>(value: T): T {
  if (value == null) return value;
  if (Array.isArray(value)) return [...value] as T;
  return value;
}

export function resetMapTimeSeriesRuntime(): void {
  if (mapTimeSeriesRuntime.updateTimeout) {
    clearTimeout(mapTimeSeriesRuntime.updateTimeout);
    mapTimeSeriesRuntime.updateTimeout = null;
  }
  mapTimeSeriesRuntime.lastSelectedEvents = [];
  mapTimeSeriesRuntime.lastSelectedRange = [];
  mapTimeSeriesRuntime.lastSelectedRanges = [];
  mapTimeSeriesRuntime.lastCutEvents = [];
  mapTimeSeriesRuntime.lastMaptype = '';
  mapTimeSeriesRuntime.lastTheme = '';
  mapTimeSeriesRuntime.lastValuesLength = 0;
  mapTimeSeriesRuntime.lastMapDataLength = 0;
  mapTimeSeriesRuntime.lastTimelineFilterSig = '';
  mapTimeSeriesRuntime.lastSelectedTime = null;
}

/** Signature for race/leg timeline filtering (map chart + track payload). */
export function timelineFilterSignature(
  races: unknown[],
  legs: unknown[],
  displayedData: unknown[],
): string {
  const first = displayedData[0] as { Datetime?: unknown; timestamp?: unknown } | undefined;
  const last = displayedData[displayedData.length - 1] as { Datetime?: unknown; timestamp?: unknown } | undefined;
  return JSON.stringify([
    Array.isArray(races) ? races : [],
    Array.isArray(legs) ? legs : [],
    displayedData.length,
    first?.Datetime ?? first?.timestamp ?? null,
    last?.Datetime ?? last?.timestamp ?? null,
  ]);
}
